
import { Ticket, TicketStatus, TicketPriority, FilterType, Customer, Employee, CustomerNote, PaymentStatus, InventoryItem, AppSettings, YesNoUnknown, BuybackDeviceType, DeviceModelInfo, BuybackCondition, ConditionQuestion, BuybackAccessory, InternalTicketNote, RepairStage, RepairStageStatus, CommunicationLogEntry, DashboardCardVisibilityConfig, LabelSize, SLARule, SLAPriority, RepairChecklist, Appointment, BuybackPricingFactors, TradedInDevice } from './types';

// IMPORTANT: Store sensitive credentials like API keys and IDs securely, ideally as environment variables.
// These are for demonstration with the Square Sandbox ONLY.
export const SQUARE_SANDBOX_APP_ID = 'sandbox-sq0idb-TpEBWjlk9aQU7_erjZ8JDA';
export const SQUARE_SANDBOX_LOCATION_ID = 'L1R91R8GXAZ0Q';
// The Square Access Token is for SERVER-SIDE use ONLY and should NEVER be in frontend code.

export const DEFAULT_REPAIR_STAGES_TEMPLATE: RepairStage[] = [
  { id: 'received', name: 'Device Received', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'diagnosis', name: 'Initial Diagnosis', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'estimate_sent', name: 'Estimate Sent to Customer', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'approval_wait', name: 'Awaiting Customer Approval', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'parts_order', name: 'Parts Ordering', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'repairing', name: 'Repair in Progress', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'quality_check', name: 'Final Quality Check', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'pickup_ready', name: 'Ready for Pickup', status: RepairStageStatus.PENDING, notes: '' },
  { id: 'completed_paid', name: 'Service Completed & Paid', status: RepairStageStatus.PENDING, notes: '' },
];

// Helper to generate repair progress based on ticket status for mock data
const generateMockRepairProgress = (ticketStatus: TicketStatus, isEstimateApproved?: boolean, amountDue?: number): RepairStage[] => {
  const progress = JSON.parse(JSON.stringify(DEFAULT_REPAIR_STAGES_TEMPLATE)) as RepairStage[];
  const now = new Date().toISOString();

  const updateStage = (id: string, status: RepairStageStatus, setTime: boolean = true) => {
    const stage = progress.find(s => s.id === id);
    if (stage) {
      stage.status = status;
      if (setTime) {
        if (status === RepairStageStatus.COMPLETED || status === RepairStageStatus.SKIPPED) stage.completedAt = now;
        else if (status === RepairStageStatus.IN_PROGRESS && !stage.startedAt) stage.startedAt = now;
      }
    }
  };

  const completeUpTo = (stageId: string) => {
     const stageIndex = progress.findIndex(s => s.id === stageId);
     for (let i = 0; i <= stageIndex; i++) {
        if(progress[i].status === RepairStageStatus.PENDING) {
            updateStage(progress[i].id, RepairStageStatus.COMPLETED);
        }
     }
  };

  updateStage('received', RepairStageStatus.COMPLETED);

  switch (ticketStatus) {
    case TicketStatus.NEW:
      updateStage('diagnosis', RepairStageStatus.IN_PROGRESS);
      if (amountDue === 0) { // No estimate needed if $0 due initially
          updateStage('estimate_sent', RepairStageStatus.SKIPPED);
          updateStage('approval_wait', RepairStageStatus.SKIPPED);
      }
      break;
    case TicketStatus.IN_PROGRESS:
      completeUpTo('diagnosis');
      if (isEstimateApproved === true || amountDue === 0) {
        updateStage('estimate_sent', RepairStageStatus.COMPLETED);
        updateStage('approval_wait', RepairStageStatus.COMPLETED);
      } else if (isEstimateApproved === undefined && amountDue && amountDue > 0) {
        updateStage('estimate_sent', RepairStageStatus.COMPLETED);
        updateStage('approval_wait', RepairStageStatus.IN_PROGRESS);
      }
      updateStage('repairing', RepairStageStatus.IN_PROGRESS);
      break;
    case TicketStatus.WAITING_FOR_PARTS:
      completeUpTo('diagnosis');
      if (isEstimateApproved === true || amountDue === 0) {
        updateStage('estimate_sent', RepairStageStatus.COMPLETED);
        updateStage('approval_wait', RepairStageStatus.COMPLETED);
      }
      updateStage('parts_order', RepairStageStatus.IN_PROGRESS);
      break;
    case TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL:
      completeUpTo('diagnosis');
      updateStage('estimate_sent', RepairStageStatus.COMPLETED);
      updateStage('approval_wait', RepairStageStatus.IN_PROGRESS);
      break;
    case TicketStatus.READY_FOR_PICKUP:
      completeUpTo('quality_check');
       if (amountDue === 0) {
            updateStage('estimate_sent', RepairStageStatus.SKIPPED);
            updateStage('approval_wait', RepairStageStatus.SKIPPED);
       } else {
            updateStage('estimate_sent', RepairStageStatus.COMPLETED);
            updateStage('approval_wait', RepairStageStatus.COMPLETED);
       }
      updateStage('pickup_ready', RepairStageStatus.IN_PROGRESS);
      break;
    case TicketStatus.COMPLETED:
      completeUpTo('pickup_ready');
       if (amountDue === 0) { // Original amount due. If it was 0, then no estimate approval path
            updateStage('estimate_sent', RepairStageStatus.SKIPPED);
            updateStage('approval_wait', RepairStageStatus.SKIPPED);
       } else {
            updateStage('estimate_sent', RepairStageStatus.COMPLETED);
            updateStage('approval_wait', RepairStageStatus.COMPLETED);
       }
      updateStage('completed_paid', RepairStageStatus.COMPLETED);
      break;
    case TicketStatus.CUSTOMER_DECLINED:
      completeUpTo('diagnosis');
      updateStage('estimate_sent', RepairStageStatus.COMPLETED);
      updateStage('approval_wait', RepairStageStatus.COMPLETED); // Mark as completed because action taken
      // Other stages remain pending or skipped
      ['parts_order', 'repairing', 'quality_check', 'pickup_ready', 'completed_paid'].forEach(id => updateStage(id, RepairStageStatus.SKIPPED));
      break;
  }
  return progress;
};


export const MOCK_TICKETS: Ticket[] = [
  {
    id: 'TM-001',
    subject: 'Unable to connect to VPN',
    description: 'User reports intermittent connectivity issues with the company VPN. Started this morning.',
    status: TicketStatus.NEW,
    priority: TicketPriority.HIGH,
    customer: 'Alice Wonderland',
    assignedTo: 'Bob The Builder',
    createdAt: '2024-07-28T09:00:00Z',
    updatedAt: '2024-07-28T09:30:00Z',
    customerPhoneNumber: '555-0101',
    customerEmail: 'alice@example.com',
    deviceModel: 'Laptop Pro',
    amountDue: 75.00,
    paymentStatus: PaymentStatus.UNPAID,
    estimateDetails: 'Initial diagnostic fee: $75. Further investigation needed if problem persists after basic troubleshooting.',
    isEstimateApproved: undefined,
    internalNotes: [
      { id: 'IN001', text: 'Checked VPN server logs, no obvious errors. Advised user to restart modem and laptop.', createdAt: '2024-07-28T09:15:00Z', createdBy: 'Bob The Builder' },
      { id: 'IN002', text: 'User confirmed restart did not fix the issue. Will need to schedule a remote session.', createdAt: '2024-07-28T09:35:00Z', createdBy: 'Bob The Builder' }
    ],
    repairProgress: generateMockRepairProgress(TicketStatus.NEW, undefined, 75.00),
    communicationLog: [],
    category: 'Networking',
    aiSuggestedCategory: 'Connectivity Problem',
    aiSuggestedPriority: TicketPriority.HIGH,
    checklistProgress: {},
    partsUsed: [],
  },
  {
    id: 'TM-002',
    subject: 'Printer not working - Model X',
    description: 'Office printer Model X is not printing. Error code E42 displayed on the panel.',
    status: TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL,
    priority: TicketPriority.MEDIUM,
    customer: 'Charles Xavier',
    assignedTo: 'Carol Danvers',
    createdAt: '2024-07-27T14:15:00Z',
    updatedAt: '2024-07-28T10:00:00Z',
    customerPhoneNumber: '555-0102',
    customerEmail: 'charles@example.com',
    deviceModel: 'Office Printer X',
    amountDue: 50.50,
    paymentStatus: PaymentStatus.UNPAID,
    estimateDetails: 'Service includes: \n- Clearing paper jam mechanisms ($20.50)\n- Replacing fuser roller ($30.00 - part cost)',
    isEstimateApproved: false,
    internalNotes: [
       { id: 'IN003', text: 'Initial diagnosis suggests fuser unit failure. Part ordered. Waiting for customer approval on quote.', createdAt: '2024-07-27T15:00:00Z', createdBy: 'Carol Danvers' }
    ],
    repairProgress: generateMockRepairProgress(TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL, false, 50.50),
    communicationLog: [],
    category: 'Hardware Repair',
    aiSuggestedCategory: 'Printer Hardware Issue',
    aiSuggestedPriority: TicketPriority.MEDIUM,
    checklistProgress: {},
    partsUsed: [],
  },
  {
    id: 'TM-003',
    subject: 'Password reset request',
    description: 'User forgot their password for the internal portal and needs a reset.',
    status: TicketStatus.COMPLETED,
    priority: TicketPriority.LOW,
    customer: 'Diana Prince',
    assignedTo: 'Bruce Wayne',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), // completed 1 day later
    customerPhoneNumber: '555-0103',
    customerEmail: 'diana@example.com',
    deviceModel: 'Desktop PC',
    amountDue: 0.00,
    paymentStatus: PaymentStatus.PAID,
    internalNotes: [],
    repairProgress: generateMockRepairProgress(TicketStatus.COMPLETED, true, 0.00),
    communicationLog: [],
    category: 'Account Support',
    aiSuggestedCategory: 'Password Reset',
    aiSuggestedPriority: TicketPriority.LOW,
    checklistProgress: {},
    partsUsed: [],
  },
  {
    id: 'TM-004',
    subject: 'Software installation issue - App Suite Pro',
    description: 'User is unable to install App Suite Pro, license key not accepted.',
    status: TicketStatus.READY_FOR_PICKUP,
    priority: TicketPriority.MEDIUM,
    customer: 'Edward Nygma',
    assignedTo: undefined,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 - 5 * 60 * 60 * 1000).toISOString(), // ~2 days 5 hours ago
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // updated 1 hour ago
    customerPhoneNumber: '555-0104',
    customerEmail: 'edward@example.com',
    deviceModel: 'Surface Tablet',
    amountDue: 120.00,
    paymentStatus: PaymentStatus.UNPAID,
    estimateDetails: 'Software troubleshooting and license activation: $120.00',
    isEstimateApproved: true,
    internalNotes: [],
    repairProgress: generateMockRepairProgress(TicketStatus.READY_FOR_PICKUP, true, 120.00),
    communicationLog: [],
    category: 'Software Problem',
    checklistProgress: {},
    partsUsed: [],
  },
  {
    id: 'TM-005',
    subject: 'Email sync problems on mobile',
    description: 'User reports that emails are not syncing correctly on their mobile device after the latest OS update.',
    status: TicketStatus.COMPLETED,
    priority: TicketPriority.HIGH,
    customer: 'Fiona Glenanne',
    assignedTo: 'Bob The Builder',
    createdAt: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(), // 10 hours ago
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // completed 9 hours later (1 hour ago)
    customerPhoneNumber: '555-0105',
    customerEmail: 'fiona@example.com',
    deviceModel: 'Smartphone Z',
    amountDue: 0.00,
    paymentStatus: PaymentStatus.PAID,
    internalNotes: [],
    repairProgress: generateMockRepairProgress(TicketStatus.COMPLETED, true, 0.00),
    communicationLog: [],
    category: 'Software Problem',
    checklistProgress: {},
    partsUsed: [],
  },
  {
    id: 'TM-006',
    subject: 'Request for new monitor',
    description: 'User requests a new dual monitor setup for improved productivity.',
    status: TicketStatus.CUSTOMER_DECLINED,
    priority: TicketPriority.LOW,
    customer: 'George Costanza',
    assignedTo: undefined,
    createdAt: '2024-07-29T09:00:00Z',
    updatedAt: '2024-07-29T09:00:00Z',
    customerPhoneNumber: '555-0106',
    customerEmail: 'george@example.com',
    deviceModel: 'N/A',
    amountDue: 250.00,
    paymentStatus: PaymentStatus.UNPAID,
    estimateDetails: 'Dual monitor setup (2 x 24" monitors + installation): $250.00',
    isEstimateApproved: false,
    internalNotes: [],
    repairProgress: generateMockRepairProgress(TicketStatus.CUSTOMER_DECLINED, false, 250.00),
    communicationLog: [],
    category: 'Hardware Request',
    checklistProgress: {},
    partsUsed: [],
  },
  {
    id: 'TM-007',
    subject: 'Cannot access shared drive X',
    description: 'User gets permission denied error when trying to access \\\\server\\sharedX.',
    status: TicketStatus.IN_PROGRESS, // Changed to IN_PROGRESS for SLA testing
    priority: TicketPriority.MEDIUM,
    customer: 'Harry Potter',
    assignedTo: 'Carol Danvers',
    createdAt: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(), // 30 hours ago
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // updated 2 hours ago
    customerPhoneNumber: '555-0107',
    customerEmail: 'harry@example.com',
    deviceModel: 'Workstation HP',
    amountDue: 30.00,
    paymentStatus: PaymentStatus.UNPAID, // For SLA testing
    internalNotes: [],
    repairProgress: generateMockRepairProgress(TicketStatus.IN_PROGRESS, true, 30.00),
    communicationLog: [],
    category: 'Access Issue',
    checklistProgress: {},
    partsUsed: [],
  },
   {
    id: 'TM-008',
    subject: 'Cracked iPhone Screen',
    description: 'Customer dropped their iPhone 12, screen is shattered. Touch sometimes unresponsive.',
    status: TicketStatus.WAITING_FOR_PARTS,
    priority: TicketPriority.HIGH,
    customer: 'Alice Wonderland',
    assignedTo: 'Carol Danvers',
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // updated 1 hour ago
    customerPhoneNumber: '555-0101',
    customerEmail: 'alice@example.com',
    deviceModel: 'iPhone 12',
    deviceSerialNumber: 'ABC123XYZ',
    devicePasscode: '1234',
    dataBackupStatus: YesNoUnknown.NO,
    findMyDeviceDisabled: YesNoUnknown.YES,
    amountDue: 149.99,
    paymentStatus: PaymentStatus.UNPAID,
    estimateDetails: 'iPhone 12 Screen Replacement (OEM Quality Part): $149.99.\nIncludes labor and 90-day warranty on the replaced part.',
    isEstimateApproved: true,
    internalNotes: [],
    repairProgress: generateMockRepairProgress(TicketStatus.WAITING_FOR_PARTS, true, 149.99),
    communicationLog: [],
    category: 'Screen Repair',
    aiSuggestedCategory: 'Screen Damage',
    aiSuggestedPriority: TicketPriority.HIGH,
    checklistProgress: {},
    partsUsed: [
      {
        id: 'PU-001',
        inventoryItemId: 'INV-SVC-004',
        itemName: 'iPhone 12 Screen (OEM)',
        quantityUsed: 1,
        unitCostAtTimeOfUse: 70.00,
        unitPriceAtTimeOfUse: 149.99,
        addedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        addedBy: 'Carol Danvers',
      }
    ],
  }
];

export const STATUS_FILTERS: FilterType[] = [
  'ALL',
  TicketStatus.NEW,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_FOR_PARTS,
  TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL,
  TicketStatus.READY_FOR_PICKUP,
  TicketStatus.COMPLETED,
  TicketStatus.CUSTOMER_DECLINED,
];

// Derive MOCK_CUSTOMERS from MOCK_TICKETS to ensure consistency
const uniqueCustomerNames = new Set<string>();
export const MOCK_CUSTOMERS: Customer[] = MOCK_TICKETS.reduce((acc: Customer[], ticket) => {
  if (!uniqueCustomerNames.has(ticket.customer)) {
    uniqueCustomerNames.add(ticket.customer);
    acc.push({
      id: `CUST-${String(acc.length + 1).padStart(3, '0')}`,
      name: ticket.customer,
      phoneNumber: ticket.customerPhoneNumber,
      email: ticket.customerEmail,
      createdAt: ticket.createdAt,
      notes: [
        { text: `Initial profile created with ticket ${ticket.id}.`, date: new Date().toISOString() }
      ],
    });
  }
  return acc;
}, []);


export const MOCK_EMPLOYEES: Employee[] = [
  { id: 'EMP-001', name: 'Bob The Builder', role: 'Senior Technician', email: 'bob@techmedics.com', phone: '555-1111', status: 'Active', lastLogin: '2024-07-29T08:30:00Z' },
  { id: 'EMP-002', name: 'Carol Danvers', role: 'Technician', email: 'carol@techmedics.com', phone: '555-2222', status: 'Active', lastLogin: '2024-07-28T14:00:00Z' },
  { id: 'EMP-003', name: 'Bruce Wayne', role: 'Lead Technician', email: 'bruce@techmedics.com', phone: '555-3333', status: 'Active', lastLogin: '2024-07-29T09:15:00Z' },
  { id: 'EMP-004', name: 'Admin User', role: 'Administrator', email: 'admin@techmedics.com', phone: '555-0000', status: 'Active', lastLogin: '2024-07-29T10:00:00Z' },
  { id: 'EMP-005', name: 'Inactive User', role: 'Technician', email: 'inactive@techmedics.com', phone: '555-4444', status: 'Inactive', lastLogin: '2024-06-01T10:00:00Z' },
];

export const MOCK_INVENTORY_ITEMS: InventoryItem[] = [
  { id: 'INV-SQ-001', name: 'iPhone 13 Screen Assembly (Square)', sku: 'IP13-SCR-SQ', category: 'Screens', stock: 15, price: 129.99, cost: 65.00, supplier: 'ScreenMasters Inc.', lastSyncedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), source: 'square', usageType: 'sale' },
  { id: 'INV-SQ-002', name: 'iPhone 13 Battery (Square)', sku: 'IP13-BAT-SQ', category: 'Batteries', stock: 25, price: 49.99, cost: 20.00, supplier: 'PowerUp Ltd.', lastSyncedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), source: 'square', usageType: 'sale' },
  { id: 'INV-SVC-001', name: 'MacBook Pro 14" Logic Board', sku: 'MBP14-LB-SVC', category: 'Logic Boards', stock: 3, cost: 250.00, supplier: 'Apple Parts Direct', source: 'service', notes: 'For internal use only, AASP stock.', usageType: 'repair' },
  { id: 'INV-SVC-002', name: 'Generic USB-C Charging Cable', sku: 'USBC-CBL-SVC', category: 'Cables', stock: 50, cost: 2.50, source: 'service', price: 9.99, usageType: 'general' },
  { id: 'INV-SQ-003', name: 'Display Adhesive Strips (iPhone 12/13) (Square)', sku: 'ADH-IP1213-SQ', category: 'Tools & Supplies', stock: 100, price: 5.99, cost: 1.00, supplier: 'FixItSupplies', lastSyncedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), source: 'square', usageType: 'sale' },
  { id: 'INV-SVC-003', name: 'Pentalobe Screwdriver P5', sku: 'TOOL-P5-SVC', category: 'Tools', stock: 5, cost: 7.00, source: 'service', notes: 'Technician tool.', usageType: 'general'},
  { id: 'INV-SVC-004', name: 'iPhone 12 Screen (OEM)', sku: 'IP12-SCR-OEM', category: 'Screens', stock: 8, cost: 70.00, supplier: 'OEM Parts Co.', source: 'service', usageType: 'repair', price: 149.99 },
  { id: 'INV-SVC-005', name: 'Cleaning Solution (500ml)', sku: 'CLEAN-500ML', category: 'Supplies', stock: 12, cost: 5.00, source: 'service', usageType: 'general', price: 10.00 },
];

const DEFAULT_DASHBOARD_CARD_VISIBILITY: DashboardCardVisibilityConfig = {
  activeRepairs: true,
  revenueSummary: true,
  newRepairsToday: true,
  newTickets: true,
  inProgressTickets: true,
  waitingCustomerApprovalTickets: true,
  waitingForPartsTickets: true,
  readyForPickupTickets: true,
  quickAppraisalWidget: true,
  repairTypeBreakdown: true,
  newCheckInsPanel: true,
};

export const DEFAULT_APP_SETTINGS: AppSettings = {
  companyName: 'Tech Medics Inc.',
  companyAddress: '3764 Eisenhower Parkway, Macon Ga 31206',
  companyPhone: '478-259-6371',
  companyWebsite: 'www.MyTechMedics.com',
  defaultTicketPriority: TicketPriority.MEDIUM,
  intakeAgreementText: `By signing this document customer agrees to allow Tech Medics to perform service on listed device above. Customer understands that Tech Medics is not responsible for any data loss that may occur while in possession of the device listed. Tech Medics will contact you 3 times within a 30 day period when the device is ready for pickup. After the 31st Day if full balance is unpaid the device will be marked abandoned. Tech Medics will then take ownership of the device or the device may be recycled. Please sign below agreeing to these terms. Replaced parts, such as damaged screens, are typically retained by Tech Medics for proper disposal or recycling. If you wish to keep your original (replaced) screen, a $40 handling fee will apply.`,
  completionAgreementText: `By signing this document customer agrees that Tech Medics has completed the service(s) listed for the device(s) above. Customer understands that Tech Medics is not responsible for any data loss that may have occurred while in possession of the device(s). Tech Medics will warranty work performed on the device(s) listed above for 30 days from the day of pickup. This warranty does not cover accidental damage caused by customer to service part or device listed.`,
  logoBase64: null,
  primaryColor: '#003794', // Tech Medics Blue
  accentColor: '#FFa800',  // Tech Medics Yellow
  dashboardCardVisibility: DEFAULT_DASHBOARD_CARD_VISIBILITY,
};


// --- Buyback Widget Constants (Apple Only) ---
export const BUYBACK_DEVICE_TYPES: BuybackDeviceType[] = Object.values(BuybackDeviceType);

export const INITIAL_BUYBACK_APPLE_MODELS_BY_TYPE: Record<BuybackDeviceType, DeviceModelInfo[]> = {
  [BuybackDeviceType.PHONE]: [
    { id: 'iphone15promax', name: 'iPhone 15 Pro Max', baseValue: 700, storageOptions: ['256GB', '512GB', '1TB'] },
    { id: 'iphone15pro', name: 'iPhone 15 Pro', baseValue: 650, storageOptions: ['128GB', '256GB', '512GB', '1TB'] },
    { id: 'iphone15plus', name: 'iPhone 15 Plus', baseValue: 550, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone15', name: 'iPhone 15', baseValue: 500, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone14promax', name: 'iPhone 14 Pro Max', baseValue: 600, storageOptions: ['128GB', '256GB', '512GB', '1TB'] },
    { id: 'iphone14pro', name: 'iPhone 14 Pro', baseValue: 550, storageOptions: ['128GB', '256GB', '512GB', '1TB'] },
    { id: 'iphone14plus', name: 'iPhone 14 Plus', baseValue: 480, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone14', name: 'iPhone 14', baseValue: 450, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphonese3', name: 'iPhone SE (3rd gen)', baseValue: 200, storageOptions: ['64GB', '128GB', '256GB'] },
    { id: 'iphone13promax', name: 'iPhone 13 Pro Max', baseValue: 500, storageOptions: ['128GB', '256GB', '512GB', '1TB'] },
    { id: 'iphone13pro', name: 'iPhone 13 Pro', baseValue: 450, storageOptions: ['128GB', '256GB', '512GB', '1TB'] },
    { id: 'iphone13', name: 'iPhone 13', baseValue: 380, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone13mini', name: 'iPhone 13 mini', baseValue: 320, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone12promax', name: 'iPhone 12 Pro Max', baseValue: 400, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone12pro', name: 'iPhone 12 Pro', baseValue: 350, storageOptions: ['128GB', '256GB', '512GB'] },
    { id: 'iphone12', name: 'iPhone 12', baseValue: 280, storageOptions: ['64GB', '128GB', '256GB'] },
    { id: 'iphone12mini', name: 'iPhone 12 mini', baseValue: 230, storageOptions: ['64GB', '128GB', '256GB'] },
    { id: 'iphonese2', name: 'iPhone SE (2nd gen)', baseValue: 120, storageOptions: ['64GB', '128GB', '256GB'] },
    { id: 'iphone11promax', name: 'iPhone 11 Pro Max', baseValue: 280, storageOptions: ['64GB', '256GB', '512GB'] },
    { id: 'iphone11pro', name: 'iPhone 11 Pro', baseValue: 250, storageOptions: ['64GB', '256GB', '512GB'] },
    { id: 'iphone11', name: 'iPhone 11', baseValue: 200, storageOptions: ['64GB', '128GB', '256GB'] },
  ],
  [BuybackDeviceType.TABLET]: [
    { id: 'ipadpro129m2', name: 'iPad Pro 12.9" (M2, 6th gen)', baseValue: 700, storageOptions: ['128GB', '256GB', '512GB', '1TB', '2TB'] },
    { id: 'ipadpro11m2', name: 'iPad Pro 11" (M2, 4th gen)', baseValue: 550, storageOptions: ['128GB', '256GB', '512GB', '1TB', '2TB'] },
    { id: 'ipadair5', name: 'iPad Air (M1, 5th gen)', baseValue: 400, storageOptions: ['64GB', '256GB'] },
    { id: 'ipad10', name: 'iPad (10th gen)', baseValue: 280, storageOptions: ['64GB', '256GB'] },
    { id: 'ipadmini6', name: 'iPad mini (6th gen)', baseValue: 300, storageOptions: ['64GB', '256GB'] },
    { id: 'ipadpro129m1', name: 'iPad Pro 12.9" (M1, 5th gen)', baseValue: 600, storageOptions: ['128GB', '256GB', '512GB', '1TB', '2TB'] },
    { id: 'ipadpro11m1', name: 'iPad Pro 11" (M1, 3rd gen)', baseValue: 480, storageOptions: ['128GB', '256GB', '512GB', '1TB', '2TB'] },
    { id: 'ipadair4', name: 'iPad Air (4th gen)', baseValue: 320, storageOptions: ['64GB', '256GB'] },
    { id: 'ipad9', name: 'iPad (9th gen)', baseValue: 200, storageOptions: ['64GB', '256GB'] },
  ],
  [BuybackDeviceType.LAPTOP]: [
    { id: 'mbp16m3max', name: 'MacBook Pro 16" (M3 Max)', baseValue: 1800, storageOptions: ['1TB SSD', '2TB SSD', '4TB SSD'] },
    { id: 'mbp16m3pro', name: 'MacBook Pro 16" (M3 Pro)', baseValue: 1500, storageOptions: ['512GB SSD', '1TB SSD', '2TB SSD'] },
    { id: 'mbp14m3max', name: 'MacBook Pro 14" (M3 Max)', baseValue: 1600, storageOptions: ['1TB SSD', '2TB SSD', '4TB SSD'] },
    { id: 'mbp14m3pro', name: 'MacBook Pro 14" (M3 Pro)', baseValue: 1300, storageOptions: ['512GB SSD', '1TB SSD', '2TB SSD'] },
    { id: 'mbp14m3', name: 'MacBook Pro 14" (M3)', baseValue: 1100, storageOptions: ['512GB SSD', '1TB SSD'] },
    { id: 'mba15m2', name: 'MacBook Air 15" (M2)', baseValue: 800, storageOptions: ['256GB SSD', '512GB SSD', '1TB SSD'] },
    { id: 'mba13m2', name: 'MacBook Air 13" (M2)', baseValue: 700, storageOptions: ['256GB SSD', '512GB SSD', '1TB SSD'] },
    { id: 'mbp13m2', name: 'MacBook Pro 13" (M2)', baseValue: 750, storageOptions: ['256GB SSD', '512GB SSD', '1TB SSD'] },
    { id: 'mba13m1', name: 'MacBook Air 13" (M1)', baseValue: 500, storageOptions: ['256GB SSD', '512GB SSD', '1TB SSD'] },
    { id: 'mbp13m1', name: 'MacBook Pro 13" (M1)', baseValue: 550, storageOptions: ['256GB SSD', '512GB SSD', '1TB SSD'] },
    { id: 'mbp16intel2019', name: 'MacBook Pro 16" (Intel, 2019)', baseValue: 600, storageOptions: ['512GB SSD', '1TB SSD', '2TB SSD'] },
  ],
  [BuybackDeviceType.SMARTWATCH]: [
    { id: 'awultra2', name: 'Apple Watch Ultra 2', baseValue: 450 },
    { id: 'aws9', name: 'Apple Watch Series 9', baseValue: 250, storageOptions:['41mm', '45mm'] },
    { id: 'awultra1', name: 'Apple Watch Ultra', baseValue: 380 },
    { id: 'aws8', name: 'Apple Watch Series 8', baseValue: 200, storageOptions:['41mm', '45mm'] },
    { id: 'awse2', name: 'Apple Watch SE (2nd gen)', baseValue: 150, storageOptions:['40mm', '44mm'] },
    { id: 'aws7', name: 'Apple Watch Series 7', baseValue: 170, storageOptions:['41mm', '45mm'] },
  ],
};

export const BUYBACK_CONDITIONS: BuybackCondition[] = Object.values(BuybackCondition);

export const BUYBACK_CONDITION_QUESTIONS: ConditionQuestion[] = [
  { id: 'powersOn', label: 'Device powers on and functions?', positiveImpact: true, valueAdjustmentFactor: 0.3 },
  { id: 'screenIntact', label: 'Screen is free of cracks and major display issues?', positiveImpact: true, valueAdjustmentFactor: 0.2 },
  { id: 'noMajorScratchesDents', label: 'Body has no major scratches, dents, or bends?', positiveImpact: true, valueAdjustmentFactor: 0.1 },
  { id: 'buttonsPortsFunctional', label: 'All buttons, ports, and cameras are fully functional?', positiveImpact: true, valueAdjustmentFactor: 0.1 },
  { id: 'noWaterDamage', label: 'Device has no signs of water damage?', positiveImpact: true, valueAdjustmentFactor: 0.15 },
  { id: 'notAccountLocked', label: 'Device is NOT iCloud locked (Find My Device is OFF)?', positiveImpact: true, valueAdjustmentFactor: 0.25 },
];

export const BUYBACK_ACCESSORIES_LIST: BuybackAccessory[] = Object.values(BuybackAccessory);

export const INITIAL_BUYBACK_PRICING_FACTORS: BuybackPricingFactors = {
  conditionMultiplier: {
    [BuybackCondition.LIKE_NEW]: 1.1,
    [BuybackCondition.GOOD]: 1.0,
    [BuybackCondition.FAIR]: 0.7,
    [BuybackCondition.POOR_BROKEN]: 0.2,
  },
  storageBonusPercentage: { // Percentage of base value added for higher storage
    '64GB': 0,
    '128GB': 0.05,
    '256GB': 0.10,
    '512GB': 0.15,
    '1TB': 0.20,
    '2TB': 0.25,
    // SSD for MacBooks - ensure these match options
    '256GB SSD': 0.05,
    '512GB SSD': 0.10,
    '1TB SSD': 0.15,
    '2TB SSD': 0.20,
    '4TB SSD': 0.25,
    // Apple Watch sizes (could be flat bonus or multiplier) - for now, not directly in this structure
    // These need to be handled differently, perhaps by having different baseValues for different sizes if significant
  },
  accessoryValue: {
    [BuybackAccessory.ORIGINAL_BOX]: 5,
    [BuybackAccessory.CHARGER]: 10,
    [BuybackAccessory.CABLE]: 5,
  },
  MINIMUM_POOR_VALUE: 10,
};

export const MOCK_BUYBACK_REQUESTS: any[] = [];
export const MOCK_TRADED_IN_DEVICES: TradedInDevice[] = [];


export const DYMO_LABEL_SIZES: LabelSize[] = [
  { id: 'large_general', name: 'Large General (3.5" x 2")', widthInches: 3.5, heightInches: 2, printClassName: 'label-print-3_5x2in' },
  { id: 'dymo_30252_address', name: 'Dymo 30252 Address (3.5" x 1.125")', widthInches: 3.5, heightInches: 1.125, printClassName: 'label-print-3_5x1_125in' },
  { id: 'dymo_30336_small_mp', name: 'Dymo 30336 Small MP (2.125" x 1")', widthInches: 2.125, heightInches: 1, printClassName: 'label-print-2_125x1in' },
];

export const DEFAULT_LABEL_SIZE_ID = DYMO_LABEL_SIZES[0].id; // Default to "Large General"

export const DASHBOARD_CHART_COLORS = [
  'bg-[var(--color-primary)]',
  'bg-emerald-500',
  'bg-[var(--color-accent)]',
  'bg-sky-500',
  'bg-indigo-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-pink-500',
  'bg-lime-500',
  'bg-cyan-500',
];

// SLA Configuration
export const SLA_RULES: SLARule[] = [
  { priority: TicketPriority.HIGH, respondWithinHours: 1, resolveWithinHours: 8 }, // 8 "business" hours (using wall-clock for now)
  { priority: TicketPriority.MEDIUM, respondWithinHours: 4, resolveWithinHours: 72 }, // 3 "business" days
  { priority: TicketPriority.LOW, respondWithinHours: 8, resolveWithinHours: 120 }, // 5 "business" days
];

// Threshold for when an SLA is considered "At Risk"
// e.g., 0.75 means if 75% of the allowed time has passed, it's "At Risk"
export const SLA_AT_RISK_THRESHOLD_PERCENTAGE = 0.75;

// Initialize MOCK_REPAIR_CHECKLISTS as an empty array
export const MOCK_REPAIR_CHECKLISTS: RepairChecklist[] = [];

// Appointment Scheduling Constants
export const DEFAULT_APPOINTMENT_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"
];

export const DEFAULT_APPOINTMENT_DURATIONS = [
  { label: '30 Minutes', value: 30 },
  { label: '1 Hour', value: 60 },
  { label: '1 Hour 30 Minutes', value: 90 },
  { label: '2 Hours', value: 120 },
];

export const MOCK_APPOINTMENTS: Appointment[] = [
  {
    id: 'APT-001',
    ticketId: 'TM-001',
    customerName: 'Alice Wonderland',
    serviceDescription: 'VPN Connection Diagnosis',
    date: new Date().toISOString().split('T')[0], // Today
    startTime: '10:00',
    endTime: '10:30',
    durationMinutes: 30,
    assignedTechnicianId: 'EMP-001', // Bob The Builder
    notes: 'Scheduled remote session.',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'APT-002',
    customerName: 'Fiona Glenanne',
    serviceDescription: 'Mobile Email Sync Consultation',
    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Two days from now
    startTime: '14:00',
    endTime: '15:00',
    durationMinutes: 60,
    assignedTechnicianId: 'EMP-002', // Carol Danvers
    notes: 'Follow-up consultation regarding email issues.',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];
